import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import CreateAsset from "./pages/CreateAsset";
import AssetEditor from "./pages/AssetEditor";
import AssetLibrary from "./pages/AssetLibrary";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import Subscribe from "./pages/Subscribe";
import MomentumAssets from "./pages/MomentumAssets";
import ReviewAssets from "./pages/ReviewAssets";
import OAuthCallback from "./pages/OAuthCallback";
import Connections from "./pages/Connections";
import ReferralRedirect from "./pages/ReferralRedirect";
import ShareReferral from "./pages/ShareReferral";
import AdminDashboard from "./pages/AdminDashboard";
import EmbedSlider from "./pages/EmbedSlider";

// Context
import { AuthProvider, useAuth } from "./context/AuthContext";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
        <div className="animate-pulse text-[#0a2540]">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
        <div className="animate-pulse text-[#0a2540]">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/library" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
      
      {/* Referral redirect route (public) */}
      <Route path="/r/:code" element={<ReferralRedirect />} />
      
      {/* Embed route (public - for iframe embedding) */}
      <Route path="/embed/:assetId" element={<EmbedSlider />} />
      
      {/* Protected Routes */}
      <Route path="/create" element={<ProtectedRoute><CreateAsset /></ProtectedRoute>} />
      <Route path="/editor/:assetId" element={<ProtectedRoute><AssetEditor /></ProtectedRoute>} />
      <Route path="/assets/:assetId" element={<ProtectedRoute><AssetEditor /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><AssetLibrary /></ProtectedRoute>} />
      <Route path="/subscribe" element={<ProtectedRoute><Subscribe /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
      <Route path="/oauth/callback" element={<ProtectedRoute><OAuthCallback /></ProtectedRoute>} />
      <Route path="/connections" element={<ProtectedRoute><Connections /></ProtectedRoute>} />
      <Route path="/share" element={<ProtectedRoute><ShareReferral /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      
      {/* Placeholder Routes */}
      <Route path="/momentum" element={<MomentumAssets />} />
      <Route path="/reviews" element={<ReviewAssets />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster 
          position="top-center" 
          richColors 
          toastOptions={{
            style: {
              fontFamily: 'Inter, sans-serif',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
